import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Search, Printer, Info, AlertTriangle, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, EmptyState, inputCls } from "@/components/common";
import { MANUAL, sectionText } from "@/lib/helpContent";
import { cn } from "@/lib/utils";

const Note = ({ tone, title, children }) => (
  <div className={cn("flex items-start gap-2 text-sm rounded border px-3 py-2 my-3",
    tone === "warn" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-sky-50 border-sky-200 text-sky-900")}>
    {tone === "warn" ? <AlertTriangle size={15} className="mt-0.5 shrink-0" /> : <Info size={15} className="mt-0.5 shrink-0" />}
    <div><span className="font-semibold">{title}</span> <span>{children}</span></div>
  </div>
);

function Block({ b }) {
  if (b.t === "h") return <h3 className="text-sm font-semibold text-slate-900 mt-5 mb-1.5">{b.v}</h3>;
  if (b.t === "p") return <p className="text-sm text-slate-700 leading-relaxed my-2 max-w-[70ch]">{b.v}</p>;
  if (b.t === "steps") return <ol className="my-2 space-y-1.5 max-w-[70ch]">{b.v.map((s, i) => (
    <li key={i} className="flex gap-2.5 text-sm text-slate-700"><span className="shrink-0 h-5 w-5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-semibold flex items-center justify-center tabular-nums">{i + 1}</span><span className="leading-relaxed">{s}</span></li>))}</ol>;
  if (b.t === "note") return <Note tone={b.tone} title={b.title}>{b.v}</Note>;
  if (b.t === "table") return (
    <div className="my-3 overflow-x-auto border border-slate-200 rounded">
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-50">{b.head.map((h) => <th key={h} className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{h}</th>)}</tr></thead>
        <tbody>{b.rows.map((r, i) => <tr key={i} className="border-t border-slate-100 align-top">{r.map((c, j) => <td key={j} className={cn("px-3 py-2 text-slate-700", j === 0 && "font-medium text-slate-900 whitespace-nowrap")}>{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
  if (b.t === "faq") return <dl className="my-2 divide-y divide-slate-100 border-t border-slate-100">{b.v.map((f, i) => (
    <div key={i} className="py-2.5">
      <dt className="text-sm font-semibold text-slate-900">{f.q}</dt>
      <dd className="text-sm text-slate-700 leading-relaxed mt-0.5 max-w-[70ch]">{f.a}</dd>
    </div>))}</dl>;
  return null;
}

export default function Manual() {
  const { has } = useAuth();
  const { hash } = useLocation();
  const [q, setQ] = useState("");

  const visible = useMemo(() => MANUAL.filter((s) =>
    (!s.perm || has(s.perm)) && (!s.anyPerm || s.anyPerm.some((p) => has(p)))), [has]);

  const needle = q.trim().toLowerCase();
  const shown = needle ? visible.filter((s) => sectionText(s).includes(needle)) : visible;

  useEffect(() => {
    if (!hash || needle) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, needle, shown.length]);

  return (
    <div data-testid="help-manual-page">
      <PageHeader title="User manual" subtitle="How to use TMC Learning, step by step — plus answers to the questions we are asked most. You see the sections that apply to your roles."
        actions={<button onClick={() => window.print()} className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm flex items-center gap-1.5 no-print" data-testid="help-print-btn"><Printer size={15} /> Print or save as PDF</button>} />

      <div className="grid lg:grid-cols-[240px_1fr] gap-4 items-start">
        <div className="lg:sticky lg:top-20 space-y-2 no-print">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" aria-hidden />
            <input className={cn(inputCls, "pl-8")} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the manual…" aria-label="Search the manual" data-testid="help-search-input" />
          </div>
          <nav aria-label="Manual contents" className="bg-white border border-slate-200 rounded overflow-hidden" data-testid="help-index">
            {shown.map((s) => (
              <a key={s.id} href={`#${s.id}`} className="flex items-center justify-between gap-2 px-3 py-2 text-[13px] text-slate-700 border-b border-slate-100 last:border-b-0 hover:bg-slate-50" data-testid={`help-index-${s.id}`}>
                <span><span className="block font-medium">{s.title}</span><span className="block text-[11px] text-slate-500">{s.group}</span></span>
                <ChevronRight size={13} className="text-slate-300 shrink-0" />
              </a>))}
            {!shown.length && <div className="px-3 py-2 text-xs text-slate-500">No matching section</div>}
          </nav>
        </div>

        <div className="space-y-4 min-w-0">
          {shown.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-20">
              <Panel title={s.title} hint={s.group} padded testId={`help-section-${s.id}`}>
                {s.blocks.map((b, i) => <Block key={i} b={b} />)}
              </Panel>
            </section>))}
          {!shown.length && <Panel padded><EmptyState title={`Nothing in the manual matches “${q}”`} hint="Try a single word such as certificate, quiz, overdue or SharePoint." /></Panel>}
        </div>
      </div>
    </div>
  );
}

/** Contextual help link. Drops a “?” next to a page title that jumps to the right manual section. */
export const HelpLink = ({ section, label = "How this page works" }) => (
  <a href={`/help#${section}`} title={label} aria-label={label} data-testid={`help-link-${section}`}
    className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm flex items-center gap-1.5 text-slate-600 hover:text-slate-900 no-print">
    <span className="h-4 w-4 rounded-full border border-slate-400 text-[11px] font-bold flex items-center justify-center" aria-hidden>?</span>
    <span className="hidden sm:inline">Help</span>
  </a>
);
